import { useEffect, useState } from "react";
import { Download, FileText, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SensitiveDocumentPreview, SensitiveText } from "@/components/common/Sensitive";
import { invoicePdfIframeSrc } from "@/features/invoices/upload/invoicePdfEmbedUrl";
import { isInlinePreviewableFilename } from "@/lib/kaiAttachments";
import { kaiApi } from "@/services/kaiApi";
import { cn } from "@/lib/utils";

export interface KaiConversationAttachmentRef {
  conversationId: string;
  attachmentId: string;
  filename: string;
}

interface KaiConversationAttachmentPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachment: KaiConversationAttachmentRef | null;
  previewDescription?: string;
}

function previewKind(blobType: string, filename: string): "pdf" | "image" | "other" {
  if (blobType === "application/pdf" || filename.toLowerCase().endsWith(".pdf")) {
    return "pdf";
  }
  if (blobType.startsWith("image/") || isInlinePreviewableFilename(filename)) {
    return "image";
  }
  return "other";
}

/**
 * In-app preview for files uploaded to a Kai conversation (rate-card source
 * documents, message attachments). Bytes load only when the dialog opens.
 */
export function KaiConversationAttachmentPreviewDialog({
  open,
  onOpenChange,
  attachment,
  previewDescription = "Document preview",
}: KaiConversationAttachmentPreviewDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [kind, setKind] = useState<"pdf" | "image" | "other">("other");
  const [downloading, setDownloading] = useState(false);

  const attachmentKey = attachment
    ? `${attachment.conversationId}/${attachment.attachmentId}`
    : null;

  useEffect(() => {
    if (!open || !attachment) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    setLoading(true);
    setError(null);
    setObjectUrl(null);

    kaiApi
      .getAttachmentRawUrl(attachment.conversationId, attachment.attachmentId)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setObjectUrl(url);
        setKind(previewKind("", attachment.filename));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load document");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, attachmentKey]);

  const handleDownload = async () => {
    if (!attachment) return;
    setDownloading(true);
    try {
      const url = await kaiApi.getAttachmentRawUrl(
        attachment.conversationId,
        attachment.attachmentId,
      );
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = attachment.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.warn("Failed to download attachment", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[6%] flex max-h-[88vh] w-[min(72rem,calc(100vw-2rem))] flex-col gap-3 rounded-xl border border-border bg-card p-3 shadow-2xl">
        <DialogHeader className="pr-8">
          <DialogTitle className="flex items-center gap-2 truncate">
            <FileText className="size-4 shrink-0 text-muted-foreground" />
            <span className="truncate">
              <SensitiveText value={attachment?.filename ?? "Document"} />
            </span>
          </DialogTitle>
          <DialogDescription>{previewDescription}</DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            "flex min-h-[60vh] flex-1 items-center justify-center overflow-auto rounded-lg border border-border/60 bg-background/40",
          )}
        >
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Loading document…
            </div>
          ) : error ? (
            <p className="px-6 py-10 text-center text-sm text-destructive">{error}</p>
          ) : objectUrl && kind === "pdf" ? (
            <SensitiveDocumentPreview className="h-[70vh] w-full">
              <iframe
                title={attachment?.filename ?? "Document"}
                src={invoicePdfIframeSrc(objectUrl)}
                className="h-[70vh] w-full rounded-md border-0 bg-white"
              />
            </SensitiveDocumentPreview>
          ) : objectUrl && kind === "image" ? (
            <SensitiveDocumentPreview className="max-h-[70vh] w-full">
              <img
                src={objectUrl}
                alt={attachment?.filename ?? "Document"}
                className="max-h-[70vh] w-auto rounded-md object-contain"
              />
            </SensitiveDocumentPreview>
          ) : objectUrl ? (
            <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
              <FileText className="size-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                This file can’t be previewed inline. Download it to open.
              </p>
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleDownload()}
            disabled={downloading || !attachment}
          >
            {downloading ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Download className="size-3.5" />
            )}
            Download
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default KaiConversationAttachmentPreviewDialog;
